import React, { useState, useEffect, useCallback } from "react"
import { observer, inject } from "mobx-react"
import { useTranslate, useNotify } from "react-admin"
import { makeStyles } from "@material-ui/core/styles"
import Table from "@material-ui/core/Table"
import TableHead from "@material-ui/core/TableHead"
import TableBody from "@material-ui/core/TableBody"
import TableRow from "@material-ui/core/TableRow"
import TableCell from "@material-ui/core/TableCell"
import TableContainer from "@material-ui/core/TableContainer"
import Paper from "@material-ui/core/Paper"
import Grid from "@material-ui/core/Grid"
import Button from "@material-ui/core/Button"
import Chip from "@material-ui/core/Chip"
import CircularProgress from "@material-ui/core/CircularProgress"
import Typography from "@material-ui/core/Typography"
import { requests } from "../../fetch/requests"

const useStyles = makeStyles(theme => ({
  container: { margin: "60px 24px 24px" },
  header: { display: "flex", alignItems: "center", gap: "16px", marginBottom: theme.spacing(2) },
  summary: { color: "#5b6b76" },
  url: { color: "#8a99a3", fontFamily: "monospace", fontSize: 12 },
  err: { color: "#c62828", fontFamily: "monospace", fontSize: 12 },
  num: { fontVariantNumeric: "tabular-nums" },
  loader: { display: "flex", justifyContent: "center", padding: theme.spacing(4) },
}))

const StatusChip = ({ ok, status }) => (
  <Chip
    size="small"
    label={ok ? `UP${status ? " · " + status : ""}` : `DOWN${status ? " · " + status : ""}`}
    style={{ backgroundColor: ok ? "#e6f4ea" : "#fdecea", color: ok ? "#1e7e34" : "#c62828", fontWeight: 600 }}
  />
)

const ServiceHealthList = ({ userStore }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const notify = useNotify()
  const t = key => translate(`customRoot.service_health.${key}`)

  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState({})

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const res = await requests.getServicesHealth(userStore)
      setServices(res.data || [])
    } catch (err) {
      notify("customRoot.general.error", "warning")
      setServices([])
    } finally {
      setLoading(false)
    }
  }, [userStore, notify])

  useEffect(() => { loadAll() }, [loadAll])

  const testOne = async name => {
    setTesting(prev => ({ ...prev, [name]: true }))
    try {
      const res = await requests.testServiceHealth(userStore, name)
      if (res && res.data) {
        setServices(prev => prev.map(s => (s.name === name ? res.data : s)))
      }
    } catch (err) {
      notify("customRoot.general.error", "warning")
    } finally {
      setTesting(prev => ({ ...prev, [name]: false }))
    }
  }

  const upCount = services.filter(s => s.ok).length

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h6">{t("title")}</Typography>
        {services.length > 0 && (
          <span className={classes.summary}>{t("up_summary")}: {upCount}/{services.length}</span>
        )}
        <Button variant="contained" color="primary" onClick={loadAll} disabled={loading}>{t("test_all")}</Button>
      </div>

      <TableContainer component={Paper}>
        {loading ? (
          <div className={classes.loader}><CircularProgress /></div>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("service")}</TableCell>
                <TableCell>{t("status")}</TableCell>
                <TableCell>{t("response_time")}</TableCell>
                <TableCell>{t("url")}</TableCell>
                <TableCell>{t("error")}</TableCell>
                <TableCell align="center">{t("action")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {services.length === 0 && (
                <TableRow><TableCell colSpan={6} align="center">{t("no_data")}</TableCell></TableRow>
              )}
              {services.map(s => (
                <TableRow key={s.name}>
                  <TableCell>{s.label || s.name}</TableCell>
                  <TableCell><StatusChip ok={s.ok} status={s.status} /></TableCell>
                  <TableCell className={classes.num}>{typeof s.responseMs === "number" ? `${s.responseMs} ms` : ""}</TableCell>
                  <TableCell className={classes.url}>{s.url || ""}</TableCell>
                  <TableCell className={classes.err}>{s.error || ""}</TableCell>
                  <TableCell align="center">
                    <Button size="small" onClick={() => testOne(s.name)} disabled={!!testing[s.name]}>
                      {testing[s.name] ? <CircularProgress size={16} /> : t("test")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </div>
  )
}

export default inject("userStore")(observer(ServiceHealthList))
